import { inject, Injectable, InjectionToken } from '@angular/core';
import { DateTime } from 'luxon';
import { TimeSkewService } from './time-skew.service';

/**
 * Configuration interface for authentication timeouts and thresholds
 */
export interface AuthConfig {
    // Token refresh thresholds
    minimumRefreshBufferMs: number; // Minimum time before expiry to refresh

    // Wake detection and network readiness
    wakeDetectionThresholdMs: number;
    networkReadyGracePeriodMs: number;
    maxNetworkWaitMs: number;

    // Retry configuration
    maxRetryAttempts: number;
    retryBaseDelayMs: number;
    networkErrorDelayMs: number;

    // Timeout protection
    deferredRefreshTimeoutMs: number;
    networkConnectivityTimeoutMs: number;
}

/**
 * Default authentication configuration
 */
const DEFAULT_AUTH_CONFIG: AuthConfig = {
    // Token refresh configuration - refresh when 3 minutes remain
    minimumRefreshBufferMs: 180000, // 3 minutes buffer before expiry

    // Wake detection and network readiness
    wakeDetectionThresholdMs: 5000, // 5+ second gap indicates wake from sleep
    networkReadyGracePeriodMs: 2000, // Grace period for network stack after wake
    maxNetworkWaitMs: 15000, // Max wait for network to become ready after wake

    // Retry configuration
    maxRetryAttempts: 10, // Increased from retry(1) in original implementation
    retryBaseDelayMs: 1000, // Starting delay for exponential backoff
    networkErrorDelayMs: 2000, // Fixed delay for network errors

    // Timeout protection
    deferredRefreshTimeoutMs: 30000, // 30-second timeout cleanup prevents indefinite blocking
    networkConnectivityTimeoutMs: 3000, // Max wait for network connectivity test
};

/**
 * Injection token for providing custom auth configuration
 */
export const AUTH_CONFIG_TOKEN = new InjectionToken<Partial<AuthConfig>>('AuthConfig');

/**
 * Service responsible for providing centralized authentication configuration.
 * Allows for environment-specific overrides and testable configuration.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthConfigService {

    private readonly config: AuthConfig;
    private readonly timeSkewService = inject(TimeSkewService);

    constructor() {
        // In a real implementation, this could be injected via AUTH_CONFIG_TOKEN
        // For now, use defaults
        this.config = { ...DEFAULT_AUTH_CONFIG };
    }

    /**
     * Get the complete auth configuration
     */
    getConfig(): Readonly<AuthConfig> {
        return Object.freeze({ ...this.config });
    }

    /**
     * Get token-related configuration
     */
    getTokenConfig() {
        return {
            minimumRefreshBufferMs: this.config.minimumRefreshBufferMs
        };
    }

    /**
     * Get network-related configuration
     */
    getNetworkConfig() {
        return {
            wakeDetectionThresholdMs: this.config.wakeDetectionThresholdMs,
            readyGracePeriodMs: this.config.networkReadyGracePeriodMs,
            maxWaitMs: this.config.maxNetworkWaitMs,
            connectivityTimeoutMs: this.config.networkConnectivityTimeoutMs
        };
    }

    /**
     * Get retry-related configuration
     */
    getRetryConfig() {
        return {
            maxAttempts: this.config.maxRetryAttempts,
            baseDelayMs: this.config.retryBaseDelayMs,
            networkErrorDelayMs: this.config.networkErrorDelayMs
        };
    }

    /**
     * Get timeout-related configuration
     */
    getTimeoutConfig() {
        return {
            deferredRefreshMs: this.config.deferredRefreshTimeoutMs,
            networkConnectivityMs: this.config.networkConnectivityTimeoutMs
        };
    }

    /**
     * Check if token refresh is required based on remaining time
     * Uses skew-adjusted time to handle clock differences between client and server
     */
    isTokenRefreshRequired(expiresAt: DateTime): boolean {
        const adjustedNow = this.timeSkewService.getAdjustedNow();
        const remainingMs = expiresAt.diff(adjustedNow).toMillis();

        // Token already expired or less than minimum buffer remaining
        return remainingMs <= this.config.minimumRefreshBufferMs;
    }

    /**
     * Update configuration at runtime (useful for testing and environment-specific overrides)
     */
    updateConfig(partialConfig: Partial<AuthConfig>): void {
        Object.assign(this.config, partialConfig);
    }
}
